import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box, CircularProgress, Container, Drawer, List, ListItemButton,
  ListItemText, Typography, Button, Alert, IconButton,
} from '@mui/material';
import { familyTheme, FAMILY_NAVY, FAMILY_CREAM, childColor } from './familyTheme';
import { parentApi } from './parentApi';
import { useAuth } from '../../contexts/AuthContext';
import BrandLogo from '../../components/BrandLogo';

const ChildContext = createContext(null);
export const useFamilyChild = () => useContext(ChildContext);

const TABS = [
  { to: '/family', label: 'Today', end: true },
  { to: '/family/classes', label: 'Classes' },
  { to: '/family/homework', label: 'Homework' },
  { to: '/family/messages', label: 'Messages' },
  { to: '/family/more', label: 'More' },
];

const TAB_ROOTS = new Set(['/family', '/family/classes', '/family/homework', '/family/messages', '/family/more']);

function isTabRoot(pathname) {
  if (TAB_ROOTS.has(pathname)) return true;
  return false;
}

function tabFallback(pathname) {
  if (pathname.startsWith('/family/classes')) return '/family/classes';
  if (pathname.startsWith('/family/homework')) return '/family/homework';
  if (pathname.startsWith('/family/messages')) return '/family/messages';
  if (pathname.startsWith('/family/leave')) return '/family/classes';
  if (pathname.startsWith('/family/more')) return '/family/more';
  return '/family';
}

export default function FamilyShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [children, setChildren] = useState([]);
  const [activeId, setActiveId] = useState(localStorage.getItem('familyActiveChildId') || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [switcher, setSwitcher] = useState(false);
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await parentApi.children();
        const list = res.data?.data || [];
        if (cancelled) return;
        setChildren(list);
        const stored = localStorage.getItem('familyActiveChildId');
        const next = list.find((c) => c.studentId === stored)?.studentId || list[0]?.studentId || '';
        setActiveId(next);
        if (next) localStorage.setItem('familyActiveChildId', next);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Could not load children.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!activeId) {
      setBatches([]);
      return undefined;
    }
    let cancelled = false;
    setBatchesLoading(true);
    parentApi.batches(activeId)
      .then((res) => {
        if (!cancelled) setBatches(res.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setBatches([]);
      })
      .finally(() => {
        if (!cancelled) setBatchesLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeId]);

  const child = children.find((c) => c.studentId === activeId) || children[0] || null;

  const selectChild = useCallback((id) => {
    setActiveId(id);
    localStorage.setItem('familyActiveChildId', id);
    setSwitcher(false);
    navigate('/family');
  }, [navigate]);

  const ctx = useMemo(
    () => ({ children, child, batches, batchesLoading, selectChild }),
    [children, child, batches, batchesLoading, selectChild]
  );

  const tab = isTabRoot(location.pathname);
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(tabFallback(location.pathname));
  };

  if (loading) {
    return (
      <ThemeProvider theme={familyTheme}>
        <Box sx={{ minHeight: '100vh', bgcolor: FAMILY_CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (error || !child) {
    return (
      <ThemeProvider theme={familyTheme}>
        <Box sx={{ p: 4, bgcolor: FAMILY_CREAM, minHeight: '100vh' }}>
          <Alert severity="error">{error || 'No children on this account.'}</Alert>
          <Button sx={{ mt: 2 }} onClick={logout}>Sign out</Button>
        </Box>
      </ThemeProvider>
    );
  }

  const initial = (child.firstName || '?').charAt(0);
  const parentInitial = (user?.email || 'P').charAt(0).toUpperCase();

  return (
    <ThemeProvider theme={familyTheme}>
      <ChildContext.Provider value={ctx}>
        <Box sx={{ minHeight: '100vh', bgcolor: FAMILY_CREAM, color: FAMILY_NAVY, pb: 10 }}>
          <Box
            sx={{
              px: 2,
              pt: 1.5,
              pb: 1.25,
              borderBottom: '1px solid rgba(27,42,74,0.08)',
              maxWidth: 560,
              mx: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {tab ? (
              <BrandLogo height={28} sx={{ mr: 0.5 }} />
            ) : (
              <IconButton aria-label="Back" onClick={goBack} size="small" sx={{ color: FAMILY_NAVY }}>
                ‹
              </IconButton>
            )}
            {tab ? (
              children.length > 1 ? (
                <Button
                  variant="outlined"
                  onClick={() => setSwitcher(true)}
                  sx={{
                    borderRadius: 999,
                    borderColor: childColor(child.studentId),
                    color: FAMILY_NAVY,
                    pl: 1,
                    minHeight: 36,
                  }}
                >
                  <Box
                    sx={{
                      width: 24, height: 24, borderRadius: '50%', bgcolor: childColor(child.studentId),
                      color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      mr: 1, fontSize: 12, fontWeight: 700,
                    }}
                  >
                    {initial}
                  </Box>
                  {child.firstName}
                </Button>
              ) : (
                <Typography fontWeight={700} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    component="span"
                    sx={{
                      width: 24, height: 24, borderRadius: '50%', bgcolor: childColor(child.studentId),
                      color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      mr: 1, fontSize: 12, fontWeight: 700,
                    }}
                  >
                    {initial}
                  </Box>
                  {child.firstName}
                </Typography>
              )
            ) : (
              <Typography fontWeight={700} sx={{ flex: 1 }} noWrap>
                {child.firstName}
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            {tab && (
              <IconButton
                aria-label="Account"
                onClick={() => navigate('/family/more')}
                sx={{ width: 36, height: 36, bgcolor: 'rgba(27,42,74,0.08)', fontSize: 14, fontWeight: 700 }}
              >
                {parentInitial}
              </IconButton>
            )}
          </Box>

          <Container maxWidth="sm" sx={{ py: 3 }}>
            <Outlet />
          </Container>

          <Box
            sx={{
              position: 'fixed', left: 0, right: 0, bottom: 0,
              borderTop: '1px solid rgba(27,42,74,0.08)', bgcolor: FAMILY_CREAM,
            }}
          >
            <Box sx={{ maxWidth: 560, mx: 'auto', display: 'flex', justifyContent: 'space-around', py: 1 }}>
              {TABS.map((item) => {
                const active = item.end
                  ? location.pathname === '/family'
                  : location.pathname.startsWith(item.to);
                return (
                  <NavLink key={item.to} to={item.to} end={item.end} style={{ textDecoration: 'none' }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: active ? 700 : 500,
                        color: active ? FAMILY_NAVY : '#5C6578',
                        px: 1, py: 1,
                      }}
                    >
                      {item.label}
                    </Typography>
                  </NavLink>
                );
              })}
            </Box>
          </Box>

          <Drawer anchor="bottom" open={switcher} onClose={() => setSwitcher(false)}>
            <Box sx={{ p: 2, pb: 4 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Who are we looking at?</Typography>
              <List>
                {children.map((c) => (
                  <ListItemButton key={c.studentId} onClick={() => selectChild(c.studentId)}>
                    <Box
                      sx={{
                        width: 36, height: 36, borderRadius: '50%', bgcolor: childColor(c.studentId),
                        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mr: 2, fontWeight: 700,
                      }}
                    >
                      {(c.firstName || '?').charAt(0)}
                    </Box>
                    <ListItemText primary={c.firstName} secondary={c.ageGroupLabel} />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          </Drawer>
        </Box>
      </ChildContext.Provider>
    </ThemeProvider>
  );
}
